import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { 
  BasePayment,
  PaymentWithDetails,
  CreatePaymentRequest, 
  UpdatePaymentRequest, 
  GetPaymentsResponse, 
} from '@/types/payment';
import { InvoiceService } from '@/services/invoice/invoiceService';
import { ContractService } from '@/services/contract/contractService';

// Add interface for the API response structure
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export class PaymentService {
  // Mock data for payments (BasePayment only - no invoice/contract)
  private static mockPayments: BasePayment[] = [
    {
      id: "pay_001",
      listingId: "68813773703045dee1bf1ec1", // Mercedes-Benz E300 (sold)
      type: "seller_payment",
      status: "paid",
      amount: 275000,
      due_date: "2024-01-25T00:00:00Z",
      paid_date: "2024-01-20T10:30:00Z",
      comment: "Utbetalning till säljare - Anna Andersson",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-15T14:00:00Z",
      updated_at: "2024-01-20T10:30:00Z"
    },
    {
      id: "pay_002",
      listingId: "68813773703045dee1bf1ec2", // BMW X5 (sold)
      type: "seller_payment",
      status: "pending",
      amount: 425000,
      due_date: "2024-01-30T00:00:00Z",
      paid_date: null,
      comment: "Utbetalning till säljare - Anna Andersson",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-10T14:00:00Z",
      updated_at: "2024-01-10T14:00:00Z"
    },
    {
      id: "pay_003",
      listingId: "68813773703045dee1bf1ec3", // Audi A4 (sold)
      type: "seller_payment",
      status: "unpaid",
      amount: 295000,
      due_date: "2024-02-05T00:00:00Z",
      paid_date: null,
      comment: "Utbetalning till säljare - Anna Andersson",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-12T14:00:00Z",
      updated_at: "2024-01-12T14:00:00Z"
    },
    {
      id: "pay_004",
      listingId: "68813773703045dee1bf1ec4", // Mercedes C-Class (sold)
      type: "seller_payment",
      status: "paid",
      amount: 375000,
      due_date: "2024-02-10T00:00:00Z",
      paid_date: "2024-02-08T14:30:00Z",
      comment: "Utbetalning till säljare - Maria Svensson",
      payout_bankDetails: {
        clearingNumber: "5678",
        accountNumber: "9012345678",
        bank: "SEB",
        accountHolder: "Maria Svensson"
      },
      created_at: "2024-01-19T14:00:00Z",
      updated_at: "2024-02-08T14:30:00Z"
    },
    {
      id: "pay_005",
      listingId: "68813773703045dee1bf1ec5", // Tesla Model 3 (sold)
      type: "seller_payment",
      status: "failed",
      amount: 325000,
      due_date: "2024-01-22T00:00:00Z",
      paid_date: null,
      comment: "Utbetalning till säljare - Anna Andersson - Banköverföring misslyckades",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-24T14:00:00Z",
      updated_at: "2024-01-24T16:30:00Z"
    },
    {
      id: "pay_006",
      listingId: "68813773703045dee1bf1ec6", // Porsche 911 (sold)
      type: "seller_payment",
      status: "paid",
      amount: 435000,
      due_date: "2024-02-01T00:00:00Z",
      paid_date: "2024-01-28T09:15:00Z",
      comment: "Utbetalning till säljare - Anna Andersson",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-24T14:00:00Z",
      updated_at: "2024-01-28T09:15:00Z"
    },
    // Buyer payments (no invoice/contract in Payment object)
    {
      id: "pay_007",
      listingId: "68813773703045dee1bf1ec1", // Mercedes-Benz E300 (sold)
      type: "buyer_payment",
      status: "overdue",
      amount: 275000,
      due_date: "2024-01-25T00:00:00Z",
      paid_date: null, // Overdue payments should not have paid_date
      comment: "Förfallen betalning - Erik Andersson",
      created_at: "2024-01-15T14:00:00Z",
      updated_at: "2024-01-25T00:00:00Z"
    },
    {
      id: "pay_008",
      listingId: "68813773703045dee1bf1ec2", // BMW X5 (sold)
      type: "buyer_payment",
      status: "pending",
      amount: 425000,
      due_date: "2024-01-30T00:00:00Z",
      paid_date: null,
      comment: "Inbetalning från köpare - Erik Andersson",
      created_at: "2024-01-10T14:00:00Z",
      updated_at: "2024-01-10T14:00:00Z"
    },
    {
      id: "pay_009",
      listingId: "68813773703045dee1bf1ec3", // Audi A4 (sold)
      type: "buyer_payment",
      status: "unpaid",
      amount: 295000,
      due_date: "2024-02-05T00:00:00Z",
      paid_date: null,
      comment: "Inbetalning från köpare - Erik Andersson",
      created_at: "2024-01-12T14:00:00Z",
      updated_at: "2024-01-12T14:00:00Z"
    },
    // Additional mock data for ec7
    {
      id: "pay_010",
      listingId: "68813773703045dee1bf1ec7", // Direct purchase listing (sold)
      type: "seller_payment",
      status: "unpaid",
      amount: 250000,
      due_date: "2024-02-10T00:00:00Z",
      paid_date: null,
      comment: "Utbetalning till säljare - Anna Andersson",
      payout_bankDetails: {
        clearingNumber: "1234",
        accountNumber: "5678901234",
        bank: "Handelsbanken",
        accountHolder: "Anna Andersson"
      },
      created_at: "2024-01-25T14:00:00Z",
      updated_at: "2024-01-25T14:00:00Z"
    },
    {
      id: "pay_011",
      listingId: "68813773703045dee1bf1ec7", // Direct purchase listing (sold)
      type: "buyer_payment",
      status: "unpaid",
      amount: 250000,
      due_date: "2024-02-10T00:00:00Z",
      paid_date: null,
      comment: "Inbetalning från köpare - Erik Andersson",
      created_at: "2024-01-25T14:00:00Z",
      updated_at: "2024-01-25T14:00:00Z"
    },
    {
      id: "pay_012",
      listingId: "68813773703045dee1bf1ec4", // Mercedes C-Class (sold)
      type: "buyer_payment",
      status: "paid",
      amount: 375000,
      due_date: "2024-02-10T00:00:00Z",
      paid_date: "2024-02-08T14:30:00Z",
      comment: "Inbetalning från köpare - Lars Johansson",
      created_at: "2024-01-19T14:00:00Z",
      updated_at: "2024-02-08T14:30:00Z"
    },
    {
      id: "pay_013",
      listingId: "68813773703045dee1bf1ec5", // Tesla Model 3 (sold)
      type: "buyer_payment",
      status: "paid",
      amount: 325000,
      due_date: "2024-01-25T00:00:00Z",
      paid_date: "2024-01-23T11:15:00Z",
      comment: "Inbetalning från köpare - Erik Andersson",
      created_at: "2024-01-15T14:00:00Z",
      updated_at: "2024-01-23T11:15:00Z"
    },
    {
      id: "pay_014",
      listingId: "68813773703045dee1bf1ec6", // Porsche 911 (sold)
      type: "buyer_payment",
      status: "paid",
      amount: 430000,
      due_date: "2024-02-05T00:00:00Z",
      paid_date: "2024-02-03T09:45:00Z",
      comment: "Inbetalning från köpare - Anna Svensson",
      created_at: "2024-01-18T14:00:00Z",
      updated_at: "2024-02-03T09:45:00Z"
    },
    {
      id: "pay_015",
      listingId: "68813773703045dee1bf1ec7", // Audi A4 (sold)
      type: "buyer_payment",
      status: "paid",
      amount: 295000,
      due_date: "2024-02-05T00:00:00Z",
      paid_date: "2024-02-03T16:20:00Z",
      comment: "Inbetalning från köpare - Lars Johansson",
      created_at: "2024-01-25T14:00:00Z",
      updated_at: "2024-02-03T16:20:00Z"
    },
    {
      id: "pay_016",
      listingId: "my_listing_003", // Mercedes C-Klass (sold)
      type: "buyer_payment",
      status: "paid",
      amount: 315000,
      due_date: "2024-01-20T00:00:00Z",
      paid_date: "2024-01-18T14:30:00Z",
      comment: "Inbetalning från köpare - Erik Johansson",
      created_at: "2024-01-10T14:00:00Z",
      updated_at: "2024-01-18T14:30:00Z"
    }
  ];

  // Get payments by auction ID and type
  // TODO: Kör på getAll men med querys bara?
  static async getPaymentsByAuction(listingId: string, type?: 'seller_payment' | 'buyer_payment'): Promise<BasePayment[]> {
      // Mock implementation - filter payments by auction ID and type
      let filteredPayments = this.mockPayments.filter(payment => payment.listingId === listingId);
      
      if (type) {
        filteredPayments = filteredPayments.filter(payment => payment.type === type);
      }

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return filteredPayments;

      // TODO: Uncomment when backend is ready
      // const response = await api.get<ApiResponse<BasePayment[]>>(`/payments/auction/${listingId}${type ? `?type=${type}` : ''}`);
      // return response.data;
  }

  // Get payments by auction with full details (joins Invoice and Contract)
  static async getPaymentsByAuctionWithDetails(listingId: string, type?: 'seller_payment' | 'buyer_payment'): Promise<PaymentWithDetails[]> {
      // Get base payments first
      const basePayments = await this.getPaymentsByAuction(listingId, type);
      
      // Convert each payment to PaymentWithDetails
      const paymentsWithDetails: PaymentWithDetails[] = await Promise.all(
        basePayments.map(async (payment) => {
          // Get invoice and contract from their respective services
          let invoice = undefined;
          let contract = undefined;

          if (payment.type === 'buyer_payment') {
              const invoices = await InvoiceService.getInvoicesByPaymentId(payment.id);
              invoice = invoices.length > 0 ? invoices[0] : undefined;

              const contracts = await ContractService.getContractsByPaymentId(payment.id);
              contract = contracts.length > 0 ? contracts[0] : undefined;
          }

          return {
            ...payment,
            invoice,
            contract
          };
        })
      );

      return paymentsWithDetails;

      // TODO: Uncomment when backend is ready
      // const response = await api.get<ApiResponse<PaymentWithDetails[]>>(`/payments/auction/${listingId}/details${type ? `?type=${type}` : ''}`);
      // return response.data;
  }

  // Get single payment with details (joins Invoice and Contract)
  static async getPayment(paymentId: string): Promise<PaymentWithDetails> {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const payment = this.mockPayments.find(p => p.id === paymentId);

      // Get invoice and contract from their respective services
      let invoice = undefined;
      let contract = undefined;

      if (payment.type === 'buyer_payment') {
          const invoices = await InvoiceService.getInvoicesByPaymentId(paymentId);
          invoice = invoices.length > 0 ? invoices[0] : undefined;

          const contracts = await ContractService.getContractsByPaymentId(paymentId);
          contract = contracts.length > 0 ? contracts[0] : undefined;
       
      }

      const paymentWithDetails: PaymentWithDetails = {
        ...payment,
        invoice,
        contract
      };

      return paymentWithDetails;

      // TODO: Uncomment when backend is ready
      // const response = await api.get<ApiResponse<PaymentWithDetails>>(`/payments/${paymentId}`);
      // return response.data;

  }

  // Get all payments with pagination
  static async getAllPayments(page: number = 1, limit: number = 10): Promise<GetPaymentsResponse> {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));

      // Mock implementation with pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedPayments = this.mockPayments.slice(startIndex, endIndex);

      const response: GetPaymentsResponse = {
        payments: paginatedPayments,
        total: this.mockPayments.length,
        page,
        limit
      };

      return response;

      // TODO: Uncomment when backend is ready
      // const response = await api.get<ApiResponse<GetPaymentsResponse>>(`/payments?page=${page}&limit=${limit}`);
      // return response.data;
  }

  // Create a new payment
  static async createPayment(data: CreatePaymentRequest): Promise<BasePayment> {
      // Mock implementation
      const newPayment: BasePayment = {
        id: `pay_${Date.now()}`,
        listingId: data.listingId,
        type: data.type,
        status: data.status,
        amount: data.amount,
        due_date: data.due_date || null,
        paid_date: data.paid_date || null,
        comment: data.comment || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      this.mockPayments.push(newPayment);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // TODO: Ska vi returna hela newPayment? Räcker bara med success här?
      return newPayment;

      // TODO: Uncomment when backend is ready
      // const response = await api.post<ApiResponse<BasePayment>>('/payments', data);
      // return response.data;
  }

  // Update an existing payment
  static async updatePayment(data: UpdatePaymentRequest): Promise<BasePayment> {
      const { id, type, ...updateData } = data;
      
      // Determine the correct endpoint based on payment type
      const endpoint = type === 'buyer_payment' 
        ? `/admin/listings/payments/buyer/${id}`
        : `/admin/listings/payments/seller/${id}`;
      
      // Prepare request body based on payment type
      let requestBody: any = {};
      
      if (type === 'buyer_payment') {
        requestBody = {
          status: updateData.status,
          amount: updateData.amount,
          due_date: updateData.due_date,
          paid_date: updateData.paid_date,
          comment: updateData.comment,
        };
        if (updateData.bidFee !== undefined) {
          requestBody.bidFee = updateData.bidFee;
        }
      } else {
        // seller_payment
        requestBody = {
          status: updateData.status,
          comment: updateData.comment,
        };
        
        // Include bank details if provided
        if (updateData.payout_bankDetails) {
          requestBody.bankName = updateData.payout_bankDetails.bank;
          requestBody.accountNumber = updateData.payout_bankDetails.accountNumber;
          requestBody.clearingNumber = updateData.payout_bankDetails.clearingNumber;
          requestBody.accountHolder = updateData.payout_bankDetails.accountHolder;
        }
        
        // Include amount fields if provided (for seller payment, amount maps to grossAmount)
        if (updateData.amount !== undefined) {
          requestBody.grossAmount = updateData.amount;
        }
        if (updateData.sellerFeePercentage !== undefined) {
          requestBody.sellerFeePercentage = updateData.sellerFeePercentage;
        }
        if (updateData.dealerFee !== undefined) {
          requestBody.dealerFee = updateData.dealerFee;
        }
        if (updateData.netAmount !== undefined) {
          requestBody.netAmount = updateData.netAmount;
        }
      }
      
      const response = await api.put<ApiResponse<BasePayment>>(endpoint, requestBody);
      
      // Backend returns { success: true, data: BasePayment }
      // The response.data is the BasePayment DTO already formatted
      const backendPayment = response.data;
      
      // Return the payment with listingId from request (backend doesn't include it in response)
      return {
        ...backendPayment,
        listingId: data.listingId,
        type: type, // Ensure type matches request
      };
  }

  // Delete a payment
  static async deletePayment(paymentId: string): Promise<void> {
      // Mock implementation
      const paymentIndex = this.mockPayments.findIndex(p => p.id === paymentId);
      
      this.mockPayments.splice(paymentIndex, 1);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 100));

      // TODO: Uncomment when backend is ready
      // await api.delete(`/payments/${paymentId}`);
  }
}
